import {Component, ViewChild} from '@angular/core';
import {
    MvsCrudObjectComponent
} from '../../../../core/shared/crud/components/mvs-crud-object/mvs-crud-object.component';
import {
    MvsFormStateDto
} from '../../../../core/shared/crud/components/mvs-crud-object-form/dto/mvs-form-state.dto';
import {DtoDetail} from '../../../../core/shared/dto/dto.detail';
import {
    ObjectChangeInformation,
    ObjectChangeInformationActionEnum
} from '../../../../core/shared/logic/object-change-information';

/** One entry of the event log — every output the form fired, newest first. */
interface XxFormEventLogEntry {
    /** Running number, so two events in the same millisecond stay distinguishable. */
    seq: number;
    /** hh:mm:ss.SSS of the moment the event arrived. */
    time: string;
    /** Output name, e.g. "onFormStateChange". */
    name: string;
    /** German one-line summary of the payload. */
    detail: string;
    /** Drives the colour of the row marker. */
    severity: 'info' | 'success' | 'warn' | 'danger';
}

/** Object types offered in the selector — all registered in xx.entity-provider.ts. */
const OBJECT_TYPE_OPTIONS = [
    {
        label: 'xx.XxSimpleTestEntity — viele Pflichtfelder mit Min/Max',
        value: 'xx.XxSimpleTestEntity'
    },
    {
        label: 'xx.XxPerson — schlanke Entity',
        value: 'xx.XxPerson'
    },
    {
        label: 'xx.XxGenericTest — mit generischen Attributen',
        value: 'xx.XxGenericTest'
    }
];

/**
 * Test page for the CALLER-OWNED SAVE constellation (route `xx/test/external-save`).
 *
 * The question this page answers: can a component embed a generated form, suppress its footer, keep
 * its own "Speichern" button, and still let the FORM do the actual write — while knowing at every
 * moment whether the data is changed, valid and complete?
 *
 * Everything visible here uses only the public contract of `<mvs-crud-object>`:
 *
 *   [hideDefaultActions]  the form renders no Anlegen / Speichern / Löschen button
 *   (onFormStateChange)   full snapshot — dirty, valid, complete, busy, disabled, readonly,
 *                         createMode, submittable (see MvsFormStateDto)
 *   (onFormDirty)         the pre-existing narrow output, logged alongside to show it agrees
 *   (onFormSubmitStateChange) the pre-existing narrow output, ditto
 *   save()                triggers the very same write the built-in button performs
 *   (onChangedObject) / (onObjectChangeError)   the outcome
 *
 * The page deliberately offers a SECOND button that calls `save()` without checking `submittable`,
 * so the refusal path is observable too: the form marks its fields as touched, shows the standard
 * error toast and `save()` returns false — the caller never has to re-implement a field check.
 *
 * No ticket — exploratory test surface for the form framework.
 */
@Component({
    selector: 'mvs-xx-form-external-save-test',
    templateUrl: './xx-form-external-save-test.page.html',
    styleUrls: ['./xx-form-external-save-test.page.scss'],
    standalone: false
})
export class XxFormExternalSaveTestPage {

    /** The embedded form — the handle the whole exercise is about. */
    @ViewChild('crudObject') crudObject: MvsCrudObjectComponent;

    readonly objectTypeOptions = OBJECT_TYPE_OPTIONS;

    /** Entity the form is generated for. */
    objectType: string = OBJECT_TYPE_OPTIONS[0].value;

    /** Empty / 0 => create mode. Any other id => edit mode on that row. */
    objectId: number = null;

    /** The switches the page drives the form with. */
    hideDefaultActions: boolean = true;
    disabled: boolean = false;
    readonlyForm: boolean = false;

    /**
     * Bumped on every "Formular neu aufbauen" — used as an @if key so the whole component is
     * destroyed and recreated, which is the only way to prove the state starts clean again.
     */
    formInstance: number = 1;

    /** Latest snapshot from (onFormStateChange). Null until the form published its first one. */
    state: MvsFormStateDto = null;

    /** Latest values of the two narrow outputs — shown next to the snapshot so drift is visible. */
    dirtyOutput: boolean = null;
    submitStateOutput: boolean = null;

    /** Latest (onFormChange) payload — what a save would post. */
    lastFormValue: DtoDetail = null;

    /** Outcome banner. */
    resultMessage: string = null;
    errorMessage: string = null;

    /** What the last save() call returned — the caller's own verdict. */
    lastTriggerResult: string = null;

    eventLog: XxFormEventLogEntry[] = [];

    private seq: number = 0;

    // ----------------------------------------------------------------------------------- //
    // THE POINT OF THE PAGE: the caller decides WHEN to save
    // ----------------------------------------------------------------------------------- //

    /**
     * The caller's own save button. Gated on the form's own verdict, so it behaves exactly like the
     * built-in one — but it lives outside the form and the page decides when it may be pressed.
     */
    handleSave(): void {

        this.resultMessage = null;
        this.errorMessage = null;

        const started = this.crudObject.save();

        this.lastTriggerResult = started
            ? 'save() => true — Schreibvorgang gestartet'
            : 'save() => false — Formular hat abgelehnt';

        this.log('save()', this.lastTriggerResult, started ? 'info' : 'warn');
    }

    /**
     * Same call, but WITHOUT looking at `submittable` first. Proves the form refuses on its own:
     * every remaining field error is revealed and the write never leaves the browser.
     */
    handleForcedSave(): void {

        this.resultMessage = null;
        this.errorMessage = null;

        const started = this.crudObject.save();

        this.lastTriggerResult = started
            ? 'save() ungeprüft => true — Formular war doch bereit'
            : 'save() ungeprüft => false — Formular hat abgelehnt und die Fehler markiert';

        this.log('save() ungeprüft', this.lastTriggerResult, started ? 'info' : 'warn');
    }

    // ----------------------------------------------------------------------------------- //
    // FORM OUTPUTS
    // ----------------------------------------------------------------------------------- //

    handleFormStateChange(state: MvsFormStateDto): void {
        this.state = state;
        this.log('onFormStateChange', this.describeState(state), 'info');
    }

    handleFormDirty(dirty: boolean): void {
        this.dirtyOutput = dirty;
        this.log('onFormDirty', dirty ? 'geändert' : 'unverändert', 'info');
    }

    handleFormSubmitStateChange(submittable: boolean): void {
        this.submitStateOutput = submittable;
        this.log('onFormSubmitStateChange', submittable ? 'speicherbar' : 'nicht speicherbar', 'info');
    }

    handleFormChange(value: DtoDetail): void {
        this.lastFormValue = value;
    }

    handleChangedObject(event: ObjectChangeInformation): void {

        const id = event?.after?.['id'] ?? event?.before?.['id'] ?? '?';

        if (event?.action === ObjectChangeInformationActionEnum.created) {
            this.resultMessage = `Angelegt — id ${id}. Das Formular ist jetzt im Bearbeiten-Modus.`;
        } else if (event?.action === ObjectChangeInformationActionEnum.updated) {
            this.resultMessage = `Gespeichert — id ${id}.`;
        } else {
            this.resultMessage = `Gelöscht — id ${id}.`;
        }

        this.log('onChangedObject', this.resultMessage, 'success');
    }

    handleObjectChangeError(error: any): void {

        this.errorMessage = this.describeError(error);
        this.log('onObjectChangeError', this.errorMessage, 'danger');
    }

    // ----------------------------------------------------------------------------------- //
    // PAGE CONTROLS
    // ----------------------------------------------------------------------------------- //

    /** Rebuild the embedded component from scratch and drop every collected state. */
    handleRebuild(): void {

        this.formInstance++;
        this.resetCollectedState();
        this.log('Seite', 'Formular neu aufgebaut', 'info');
    }

    handleObjectTypeChange(): void {

        this.objectId = null;
        this.formInstance++;
        this.resetCollectedState();
    }

    handleObjectIdChange(): void {

        this.formInstance++;
        this.resetCollectedState();
    }

    handleClearLog(): void {
        this.eventLog = [];
    }

    /** True while the form is in edit mode on an existing row. */
    get editMode(): boolean {
        return this.objectId != null && this.objectId > 0;
    }

    /** Label of the caller-owned action button — mirrors what the form would have rendered. */
    get saveLabel(): string {
        return this.state?.createMode === false ? 'Speichern (Aufrufer)' : 'Anlegen (Aufrufer)';
    }

    private resetCollectedState(): void {

        this.state = null;
        this.dirtyOutput = null;
        this.submitStateOutput = null;
        this.lastFormValue = null;
        this.resultMessage = null;
        this.errorMessage = null;
        this.lastTriggerResult = null;
    }

    private describeState(state: MvsFormStateDto): string {

        return `dirty=${state.dirty} valid=${state.valid} complete=${state.complete} `
            + `busy=${state.busy} createMode=${state.createMode} submittable=${state.submittable}`;
    }

    /** The backend answers a rejected write with a list of field errors — flatten it for the log. */
    private describeError(error: any): string {

        const payload = error?.error;

        if (Array.isArray(payload) && payload.length) {
            return payload
                .map(entry => `${entry.field ?? '?'}: ${entry.defaultMessage ?? ''}`.trim())
                .join(' | ');
        }

        return error?.message ?? 'Unbekannter Fehler beim Speichern.';
    }

    private log(name: string, detail: string, severity: XxFormEventLogEntry['severity']): void {

        const now = new Date();
        const time = `${this.pad(now.getHours(), 2)}:${this.pad(now.getMinutes(), 2)}:`
            + `${this.pad(now.getSeconds(), 2)}.${this.pad(now.getMilliseconds(), 3)}`;

        this.eventLog = [
            {seq: ++this.seq, time: time, name: name, detail: detail, severity: severity},
            // keep the list short enough to stay readable while clicking around
            ...this.eventLog
        ].slice(0, 60);
    }

    private pad(value: number, length: number): string {
        return String(value).padStart(length, '0');
    }
}
